import { Logger } from '@frontend/logger';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { SlotRenderingClient } from '@frontend/slot/api';
import { SlotRendering } from '@frontend/slot/types';
import { useEffect, useState } from 'react';

import { ContainerInfo } from '../layout-geometry';
import { LayoutProps } from './layout.component';

interface ViewProps {
    modules: Module[] | null;
    slotRendering: Map<string, SlotRendering[]> | null;
    containerInfo: ContainerInfo;
}

interface LayoutBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

const emptyBounds: LayoutBounds = {
    minX: 0,
    minY: 0,
    maxX: 0,
    maxY: 0
};

const addBoxToBounds = (bounds: LayoutBounds, x: number, y: number, width: number, height: number): LayoutBounds => ({
    minX: Math.min(bounds.minX, x),
    minY: Math.min(bounds.minY, y),
    maxX: Math.max(bounds.maxX, x + width),
    maxY: Math.max(bounds.maxY, y + height)
});

const getContainerInfo = (bounds: LayoutBounds): ContainerInfo => {
    const width = Math.max(bounds.maxX - bounds.minX, 1);
    const height = Math.max(bounds.maxY - bounds.minY, 1);

    return {
        ...bounds,
        width,
        height,
        aspectRatio: height / width
    };
};

const useLayout = (props: LayoutProps): ViewProps => {
    const [modules, changeModules] = useState<Module[] | null>(props.modules || null);
    const [slotRendering, changeSlotRendering] = useState<Map<string, SlotRendering[]> | null>(null);
    const [containerInfo, changeContainerInfo] = useState<ContainerInfo>(getContainerInfo(emptyBounds));

    useEffect(() => {
        let isCurrent = true;

        if (props.spot) {
            changeSlotRendering(null);
            ModuleClient.fetchModules({ spot_id: props.spot.id })
                .then((res) => {
                    if (isCurrent) changeModules(res.results);
                })
                .catch((error) => {
                    Logger.error('Error fetching layout modules:', error);
                    if (isCurrent) changeModules([]);
                });
        }

        return () => {
            isCurrent = false;
        };
    }, [props.spot?.id]);

    useEffect(() => {
        if (props.modules) {
            changeModules(props.modules);
        }
    }, [props.modules]);

    useEffect(() => {
        if (!modules) return undefined;

        let isCurrent = true;
        let bounds = emptyBounds;
        changeSlotRendering(null);

        if (modules.length === 1) {
            bounds = addBoxToBounds(bounds, 0, 0, modules[0].dimension.width, modules[0].dimension.height);
        } else
            modules.forEach((module) => {
                bounds = addBoxToBounds(bounds, module.location.x, module.location.y, module.dimension.width, module.dimension.height);
            });

        const renderingMap: Map<string, SlotRendering[]> = new Map();
        const promises = modules.map((module) =>
            SlotRenderingClient.fetchSlotRendering(module.account_id, module.spot_id, module.id)
                .then((res) => ({
                    key: module.id,
                    value: res.results
                }))
                .catch((error) => {
                    Logger.error('Error fetching slot rendering:', error);

                    return {
                        key: module.id,
                        value: []
                    };
                })
        );

        Promise.all(promises).then((results) => {
            if (!isCurrent) return;

            results.forEach((result) => {
                renderingMap.set(result.key, result.value);
            });
            changeSlotRendering(renderingMap);

            if (modules.length === 1) {
                Array.from(renderingMap.values())
                    .flat()
                    .forEach((slotRendering) => {
                        bounds = addBoxToBounds(
                            bounds,
                            slotRendering.location.x,
                            slotRendering.location.y,
                            slotRendering.dimension.width,
                            slotRendering.dimension.height
                        );
                    });
            } else {
                Array.from(renderingMap.values())
                    .flat()
                    .forEach((slotRendering) => {
                        bounds = addBoxToBounds(
                            bounds,
                            slotRendering.absolute.x,
                            slotRendering.absolute.y,
                            slotRendering.dimension.width,
                            slotRendering.dimension.height
                        );
                    });
            }
            changeContainerInfo(getContainerInfo(bounds));
        });

        return () => {
            isCurrent = false;
        };
    }, [modules]);

    return {
        modules,
        slotRendering,
        containerInfo
    };
};

export default useLayout;
